/**
 * Ceremony API client.
 *
 * Contract source of truth: Cermony.postman_collection.json ->
 * "Public (Guest)" -> "Ceremony page":
 *
 *   GET {base_url}/ceremony/{uuid}?access_key={64-char-hex}
 *   Accept: application/json
 *
 * NOTE: this endpoint deliberately has NO "/api" prefix, unlike every other
 * endpoint in the collection (compare its path array to any admin endpoint).
 * Do not "fix" this to add /api — it would call a route that doesn't exist.
 *
 * No Authorization header is sent — the access_key query parameter IS the
 * credential for this public, no-login endpoint.
 *
 * Response envelope (from the collection's documented example):
 *   200 -> { success: true, message, data: { name, seat_number, is_checked_in, photos: [] } }
 *   404 -> { success: false, message: "Guest not found.", errors: null }
 */

import { makeError, errorKindFromStatus } from './apiError';

const BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');

/**
 * Fetch a single guest's public ceremony data.
 *
 * @param {string} ceremonyId - the guest uuid from the URL path.
 * @param {string} accessKey - the access_key query parameter.
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ name: string, seat_number: string|null, is_checked_in: boolean, photos: any[] }>}
 * @throws {{ kind: string, title: string, message: string }} a normalized error (see apiError.js)
 */
export async function getCeremony(ceremonyId, accessKey, { signal } = {}) {
  const url =
    `${BASE_URL}/ceremony/${encodeURIComponent(ceremonyId)}` +
    `?access_key=${encodeURIComponent(accessKey)}`;

  let response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
  } catch (err) {
    if (err && err.name === 'AbortError') throw err; // let callers ignore aborts
    throw makeError('network');
  }

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    if (!isJson) throw makeError(errorKindFromStatus(response.status));
    // Body is intentionally not parsed for user display — the normalized
    // copy in apiError.js is what the UI shows, regardless of message text.
    throw makeError(errorKindFromStatus(response.status));
  }

  if (!isJson) {
    // Same-origin misconfiguration guard: if the API base URL is wrong (or
    // empty on a deployment where a non-Laravel server serves the SPA at
    // this same path), a "successful" response can still be index.html
    // rather than JSON. Treat that as a server problem, not a parse crash.
    throw makeError('server');
  }

  const body = await response.json();
  if (!body || body.success !== true || !body.data) {
    throw makeError('server');
  }

  return body.data;
}
